import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { DiscoveryCard, DiscoveryFeedResult } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { initialsOf, ScreenHeader } from '../../components/common';

/**
 * Discover — compatibility-ranked cards (never compatibility-only ranking).
 * Base users see a visible remaining-request count; exhausting the lifetime
 * allowance opens the Pro screen BEFORE any request is attempted.
 */
export function DiscoverScreen() {
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<DiscoveryFeedResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState<DiscoveryCard | null>(null);
  const [note, setNote] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [saved, setSaved] = useState<string[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const socialOn = app.user?.socialEnabled === true;

  const load = useCallback(async () => {
    if (!socialOn) return;
    setBusy(true);
    setLoadFailed(false);
    try {
      setFeed(await app.backend.discoveryFeed());
    } catch (e) {
      console.warn(e);
      // A flag rather than a message string: the copy is resolved at draw time,
      // so it follows a locale change and `t` stays out of this callback's deps
      // (which would otherwise refetch the whole feed on every language switch).
      setLoadFailed(true);
    } finally {
      setBusy(false);
    }
  }, [app.backend, socialOn]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Modal keyboard contract: focus lands in the note, Escape closes, Tab stays
   * inside. Without it the caret is left on the card list behind the scrim —
   * a keyboard or screen-reader user can tab straight into buttons they cannot see.
   */
  useEffect(() => {
    if (!connecting) return;
    const opener = document.activeElement as HTMLElement | null;
    noteRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setConnecting(null);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, textarea, input, select, a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (active === (e.shiftKey ? first : last) || !dialogRef.current?.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Hand focus back to whatever opened the dialog; a no-op if that card
      // has since left the feed.
      opener?.focus?.();
    };
  }, [connecting]);

  // Private account: no feed, no silent fetching — just the opt-in pathway.
  if (!socialOn) {
    return (
      <div className="screen">
        <ScreenHeader title={t('discover.title')} />
        <div className="card col" style={{ gap: 12, marginTop: 8 }}>
          <div className="row">
            <span className="chip chip--fact" style={{ cursor: 'default' }}>
              🔒 {t('social.private.badge')}
            </span>
          </div>
          <div className="h2">{t('social.private.title')}</div>
          <div className="small muted" style={{ lineHeight: 1.6 }}>
            {t('social.private.body')}
          </div>
          <Link to="/social/enable">
            <button className="btn btn--primary btn--block">{t('social.private.cta')}</button>
          </Link>
        </div>
      </div>
    );
  }

  const startConnect = (card: DiscoveryCard) => {
    // Exhausted Base allowance → Pro upgrade BEFORE showing the compose UI.
    if (feed && feed.remainingRequests <= 0) {
      navigate('/profile/subscription');
      return;
    }
    setNote('');
    setConnecting(card);
  };

  const sendRequest = async () => {
    if (!connecting) return;
    const target = connecting;
    setConnecting(null);
    try {
      const res = await app.backend.connectionRequest(target.uid, note || undefined);
      setFeed((f) =>
        f
          ? {
              ...f,
              remainingRequests: res.quota.remaining,
              cards: f.cards.filter((c) => c.uid !== target.uid),
            }
          : f,
      );
      await app.refreshEntitlement();
      await app.refreshSocial();
    } catch (e) {
      const raw = String(e);
      if (raw.includes('quota-exhausted-base-lifetime')) navigate('/profile/subscription');
      else alert(raw.slice(0, 200));
    }
  };

  /**
   * Optimistic, but never silently wrong: a pass the server refused puts the
   * card back at its ranked position. Swallowing the failure used to hide the
   * person for this session while leaving them in tomorrow's feed.
   */
  const pass = async (card: DiscoveryCard) => {
    const at = feed?.cards.findIndex((c) => c.uid === card.uid) ?? -1;
    setActionFailed(false);
    setFeed((f) => (f ? { ...f, cards: f.cards.filter((c) => c.uid !== card.uid) } : f));
    try {
      await app.backend.discoveryAction(card.uid, 'pass');
    } catch (e) {
      console.warn(e);
      setFeed((f) => {
        if (!f || f.cards.some((c) => c.uid === card.uid)) return f;
        const cards = [...f.cards];
        cards.splice(at < 0 ? cards.length : at, 0, card);
        return { ...f, cards };
      });
      setActionFailed(true);
    }
  };

  const save = async (card: DiscoveryCard) => {
    if (saved.includes(card.uid)) return; // already stored — don't pay for a second write
    setActionFailed(false);
    setSaved((s) => [...s, card.uid]);
    try {
      await app.backend.discoveryAction(card.uid, 'save');
    } catch (e) {
      console.warn(e);
      setSaved((s) => s.filter((uid) => uid !== card.uid));
      setActionFailed(true);
    }
  };

  const quotaBanner =
    feed &&
    (feed.requestQuotaKind === 'base-lifetime'
      ? `${feed.remainingRequests} of ${app.entitlement?.config.base.lifetimeOutgoingRequests ?? 5} lifetime ${t('discover.remaining')}`
      : feed.remainingRequests <= 3
        ? `${feed.remainingRequests} ${t('discover.remaining')} today`
        : null);

  return (
    <div className="screen">
      <ScreenHeader title={t('discover.title')} subtitle={t('discover.subtitle')} />

      {quotaBanner && (
        <div className="banner banner--info small" style={{ marginBottom: 12 }}>
          {quotaBanner}
          {feed?.requestQuotaKind === 'base-lifetime' && feed.remainingRequests <= 1 && (
            <Link to="/profile/subscription"> · {t('pro.upgrade')} →</Link>
          )}
        </div>
      )}

      {busy && !feed && (
        <div className="center" style={{ marginTop: 40 }}><div className="spinner" /></div>
      )}

      {/* A failed load leaves `feed` null, and every state below is gated on it —
          without this the spinner just stops and the screen stays blank forever. */}
      {loadFailed && !busy && (
        <div className="banner banner--danger small col" style={{ marginBottom: 12 }} role="status">
          <span>{t('discover.loadFailed')}</span>
          <button
            className="btn btn--ghost btn--small"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => load()}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {feed && feed.cards.length === 0 && (
        <div className="card muted small center" style={{ lineHeight: 1.6 }}>
          {t('discover.empty')}
        </div>
      )}

      <div className="col">
        {feed?.cards.map((card) => (
          <div key={card.uid} className="card fade-in">
            <div className="row" style={{ marginBottom: 8 }}>
              <div className="avatar avatar--gold" style={{ width: 52, height: 52 }}>
                {initialsOf(card.displayName)}
              </div>
              <div style={{ flex: 1 }}>
                <div className="h2">
                  {card.displayName}
                  {card.age ? <span className="muted">, {card.age}</span> : null}
                  {card.verified && <span className="tiny gold" title="verified"> ✓</span>}
                </div>
                <div className="tiny muted">
                  {card.city ?? ''}
                  {card.primaryIntention ? ` · ${card.primaryIntention.replace(/-/g, ' ')}` : ''}
                  {card.activeBand === 'today' ? ' · active today' : ''}
                </div>
              </div>
              <div className="center">
                <span className="badge badge--B" style={{ fontSize: '1rem' }}>
                  {card.compatibility.overall}
                </span>
                <div className="tiny faint">match</div>
              </div>
            </div>

            {card.bio && <div className="small muted" style={{ marginBottom: 8 }}>{card.bio}</div>}

            <div className="wrap" style={{ marginBottom: 8 }}>
              {card.compatibility.highlights.map((h) => (
                <span key={h} className="chip chip--fact" style={{ cursor: 'default' }}>{h}</span>
              ))}
              {card.interests.map((i) => (
                <span key={i} className="chip" style={{ cursor: 'default' }}>{i}</span>
              ))}
            </div>

            <div className="row">
              <button className="btn btn--primary btn--small" style={{ flex: 2 }} onClick={() => startConnect(card)}>
                {t('discover.connect')}
              </button>
              <Link to={`/discover/${card.uid}`} style={{ flex: 2 }}>
                <button className="btn btn--ghost btn--small btn--block">{t('discover.view')}</button>
              </Link>
              <button
                className="btn btn--ghost btn--small"
                style={{ flex: 1 }}
                aria-label={t('discover.save')}
                aria-pressed={saved.includes(card.uid)}
                onClick={() => save(card)}
              >
                <span aria-hidden>{saved.includes(card.uid) ? '★' : '☆'}</span>
              </button>
              <button
                className="btn btn--ghost btn--small"
                style={{ flex: 1 }}
                aria-label={t('discover.pass')}
                onClick={() => pass(card)}
              >
                <span aria-hidden>✕</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {connecting && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 90,
            display: 'grid', placeItems: 'center',
            // inset:0 keeps the scrim edge-to-edge; the padding insets only the
            // card, so it never lands under a notch or the gesture bar.
            padding:
              'calc(20px + var(--safe-top)) calc(20px + var(--safe-right)) calc(20px + var(--safe-bottom)) calc(20px + var(--safe-left))',
          }}
          onClick={() => setConnecting(null)}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${t('discover.connect')} — ${connecting.displayName}`}
            className="card card--strong col"
            style={{ width: '100%', maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h3">Connect with {connecting.displayName}</div>
            <textarea
              ref={noteRef}
              className="input"
              rows={2}
              maxLength={280}
              aria-label={t('discover.introPrompt')}
              placeholder={t('discover.introPrompt')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="tiny muted">
              This uses one connection request
              {feed?.requestQuotaKind === 'base-lifetime'
                ? ` (${feed.remainingRequests} lifetime remaining)`
                : ''}
              . It’s consumed when sent, whatever the outcome.
            </div>
            <div className="row">
              <button className="btn btn--ghost" style={{ flex: 1 }} onClick={() => setConnecting(null)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn--primary" style={{ flex: 1 }} onClick={sendRequest}>
                {t('discover.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save and pass act on a card anywhere in a long list, so the notice
          rides above the tab bar rather than scrolling away off-screen. */}
      {actionFailed && (
        <div
          className="banner banner--danger small row float-banner float-banner--toast"
          role="status"
        >
          <span style={{ flex: 1 }}>{t('common.error')}</span>
          <button className="chip" aria-label={t('common.close')} onClick={() => setActionFailed(false)}>
            <span aria-hidden>✕</span>
          </button>
        </div>
      )}
    </div>
  );
}
